import React, { useCallback, useContext, useRef } from "react";
import { Field } from "react-final-form";
import { TextField } from "components/Form/Field";
import Label from "ui/Form/Element/Label";
import { IntlContext } from "context";
import { Calendar, Clock } from "lucide-react";

type DateInputWithPicker = HTMLInputElement & { showPicker?: () => void };

const fieldLabelClassName = "text-xs font-medium text-slate-500";
const fieldInputClassName =
    "field-control__input h-12 rounded-[10px] pl-2 border-slate-200 focus-visible:ring-2 focus-visible:ring-ring date-input--placeholder";

const pickerButtonClassName =
    "absolute right-4 top-1/2 -translate-y-1/2 inline-flex h-8 w-8 items-center justify-center rounded-full text-muted-foreground transition hover:bg-accent hover:text-foreground";

const OfferStartDateSection: React.FC<{ required?: boolean }> = ({ required = true }) => {
    const intl = useContext(IntlContext);
    const startDateInputRef = useRef<DateInputWithPicker>(null);
    const startTimeInputRef = useRef<DateInputWithPicker>(null);
    const todayIso = new Date().toISOString().slice(0, 10);

    const openPicker = useCallback((input: DateInputWithPicker | null) => {
        if (!input) return;
        input.focus();
        input.showPicker?.();
    }, []);

    return (
        <section className="grid grid-cols-1 gap-3 sm:grid-cols-2">
            <TextField
                inputProps={{
                    ref: startDateInputRef,
                    name: "start_date",
                    type: "date",
                    min: todayIso,
                    required,
                    className: fieldInputClassName,
                }}
                labelProps={{
                    label: intl.formatMessage("booking.messages.offer.start_date.label"),
                    className: fieldLabelClassName,
                }}
                adornmentEnd={
                    <button
                        type="button"
                        className={pickerButtonClassName}
                        onMouseDown={(e) => e.preventDefault()}
                        onClick={() => openPicker(startDateInputRef.current)}
                        aria-label={intl.formatMessage("booking.messages.offer.start_date.label")}
                    >
                        <Calendar className="h-4 w-4" />
                    </button>
                }
            />
            <Field name="start_time">
                {({ input }) => (
                    <div className="field">
                        <Label
                            htmlFor="offer-start-time"
                            required={required}
                            className={`field__label ${fieldLabelClassName}`}
                        >
                            {intl.formatMessage("booking.messages.offer.start_time.label")}
                        </Label>
                        <div className="field-control">
                            <input
                                {...input}
                                id="offer-start-time"
                                ref={startTimeInputRef}
                                type="time"
                                required={required}
                                className={fieldInputClassName}
                            />
                            <button
                                type="button"
                                className={pickerButtonClassName}
                                onMouseDown={(e) => e.preventDefault()}
                                onClick={() => openPicker(startTimeInputRef.current)}
                                aria-label={intl.formatMessage("booking.messages.offer.start_time.label")}
                            >
                                <Clock className="h-4 w-4" />
                            </button>
                        </div>
                    </div>
                )}
            </Field>
        </section>
    );
};

export default OfferStartDateSection;
